import { Fragment } from 'react';

/**
 * ヒーローに置くターミナルのモック。
 *
 * 「作業の区切りで、AI クライアントが MCP 越しに工数を書く」という
 * 実際の動きをそのまま写す。**INOU は git hook を提供していない**ので、
 * フックが自動で発火する絵にはしない。コミットは作業の区切りとして映すだけで、
 * log_time を呼んでいるのは AI クライアントであることが読み取れる形にする。
 *
 * **台本は実際に叩いた応答に合わせてある** (設計は
 * docs/superpowers/specs/2026-08-16-landing-realism-design.md):
 *   - repository には git remote の URL をそのまま渡す (mcp/server.ts の
 *     inputSchema がそう言っている)。紐付いた行に matched_by_repository が付く
 *   - log_time が受け取るのは名前ではなく ULID の project_id。一覧の結果に
 *     出た ID がそのまま次の引数に現れる (AI が選んで渡していることが見える)
 *   - is_capitalized_log と is_billable は**引数に無いのに応答にある**。
 *     どちらもサーバーが決めるものだと、注釈ではなく行の並びで分かる
 *   - 記録した瞬間に month_planned_hours / month_actual_hours が返る
 *   - 最後に AI が「何をどう判定して入れたか」を人に返す。カテゴリは AI が
 *     会話から決めたもので、予実はサーバーが返したもの
 *
 * ULID は 26 文字あり、そのまま出すと狭い幅で必ず折り返す。先頭 10 文字 + `…`
 * に切り、一覧の結果と log_time の引数で**同じ表記**にする (同じ ID だと
 * 読めることが大事なので、切り方を揃える)。
 *
 * 行は CSS の animation-delay で上から順に出す。JS は使わない。
 */

type Line =
  | { kind: 'blank' }
  | { kind: 'cwd'; path: string; branch: string }
  | { kind: 'command'; text: string }
  | { kind: 'output'; text: string }
  | { kind: 'speaker'; text: string }
  | { kind: 'say'; text: string }
  | { kind: 'call'; tool: string }
  /** tool の引数。tool 名の下に 1 項目ずつ折る (1 行に並べると幅に入らない)。 */
  | { kind: 'arg'; text: string }
  | { kind: 'result'; text: string; emphasis?: string }
  /** 応答の 2 項目め以降。`↳` を持たず、result の本文に揃える。 */
  | { kind: 'field'; text: string }
  /** 応答に返る予実。この製品の値打ちが出る行なので色を持たせる。 */
  | { kind: 'metric'; text: string };

/** 短縮した project_id。一覧の結果と log_time の引数で同じものを出す。 */
const PROJECT_ID = '01M01J434Y…';

const LINES: Line[] = [
  { kind: 'cwd', path: '~/payments-api', branch: 'feature/refund-flow' },
  { kind: 'command', text: 'git commit -m "返金フローの検証を直す"' },
  { kind: 'output', text: '[feature/refund-flow 8f3a91c] 4 files changed' },
  { kind: 'blank' },
  { kind: 'speaker', text: 'assistant' },
  { kind: 'say', text: '返金フローの修正が終わったので記録します。' },
  { kind: 'blank' },
  { kind: 'call', tool: 'list_projects' },
  { kind: 'arg', text: 'repository=git@github.com:acme/payments-api.git' },
  { kind: 'result', text: '決済基盤', emphasis: 'matched_by_repository: true' },
  { kind: 'field', text: `project_id: ${PROJECT_ID}` },
  { kind: 'call', tool: 'log_time' },
  { kind: 'arg', text: `project_id=${PROJECT_ID}  hours=2.5` },
  { kind: 'arg', text: 'category=development' },
  { kind: 'arg', text: 'note="返金の検証を修正"' },
  { kind: 'result', text: 'is_capitalized_log: true' },
  { kind: 'field', text: 'is_billable: false' },
  { kind: 'metric', text: 'month_planned_hours: 160' },
  { kind: 'metric', text: 'month_actual_hours: 13' },
  { kind: 'blank' },
  { kind: 'speaker', text: 'assistant' },
  { kind: 'say', text: '決済基盤に 2.5h（新規開発）で記録しました。' },
  { kind: 'say', text: '今月は 160h の予定に対して 13h です。' },
];

/** 1 行あたりの間隔 (ms)。行数が増えたぶん短くし、全体は 3 秒弱に保つ。 */
const STEP_MS = 125;

function LineBody({ line }: { line: Line }) {
  switch (line.kind) {
    case 'blank':
      return <span>&nbsp;</span>;
    case 'cwd':
      return (
        <span className="text-ink-faint">
          {line.path} <span className="text-brand-300">{line.branch}</span>
        </span>
      );
    case 'command':
      return (
        <span className="text-surface-raised">
          <span aria-hidden className="text-brand-300">
            ${' '}
          </span>
          {line.text}
        </span>
      );
    case 'output':
      return <span className="text-ink-faint">{line.text}</span>;
    case 'speaker':
      return (
        <span className="text-brand-300">
          <span aria-hidden>▸ </span>
          {line.text}
        </span>
      );
    case 'say':
      return (
        <span className="text-surface-raised">
          {'  '}
          {line.text}
        </span>
      );
    case 'call':
      return (
        <span>
          <span aria-hidden className="text-brand-300">
            {'  → '}
          </span>
          <span className="text-surface-raised">{line.tool}</span>
        </span>
      );
    // 引数は tool 名の真下に、応答の続きは result の本文の真下に揃える
    case 'arg':
      return (
        <span className="text-ink-faint">
          {'    '}
          {line.text}
        </span>
      );
    case 'field':
      return (
        <span className="text-surface-raised">
          {'      '}
          {line.text}
        </span>
      );
    case 'metric':
      return (
        <span className="text-brand-300">
          {'      '}
          {line.text}
        </span>
      );
    case 'result':
      return (
        <span>
          <span aria-hidden className="text-ink-faint">
            {'    ↳ '}
          </span>
          <span className="text-surface-raised">{line.text}</span>
          {line.emphasis ? (
            <span className="text-brand-300">
              {'  '}
              {line.emphasis}
            </span>
          ) : null}
        </span>
      );
  }
}

export function McpTerminal() {
  return (
    // min-w-0 が要る。grid / flex の子は min-width: auto なので、これが無いと
    // 折り返さない pre の幅がトラックごと広がり、ページ全体が横に溢れる
    // (下の overflow-x-auto は、この指定があって初めて効く)。
    <div className="flex min-w-0 flex-col border border-line">
      <div className="flex items-center justify-between border-b border-line bg-surface-raised px-4 py-2.5">
        <span className="font-mono text-xs tracking-wider text-ink-faint">MCP SESSION</span>
        <span className="font-mono text-xs text-ink-faint">inou://timelog</span>
      </div>

      {/*
        濃色サーフェスなので ink を敷き、文字は surface-raised 側に置く。
        行は等幅で折り返さず、狭い画面では横スクロールさせる
        (折り返すとターミナルに見えなくなる)。
      */}
      {/*
        狭い幅では字送りを 1px 落とし、行間も詰める。台本の最長行は 56 桁あり、
        13px (≒ 7.8px/桁) だと 375px の画面で 8 割が画面の外に出る。
        12px + 余白を詰めると、横スクロールせずに読める幅が 2 割ほど増える
        (**行は折り返さない**。折り返すとターミナルに見えなくなる)。
      */}
      <div className="overflow-x-auto bg-ink px-4 py-4 sm:px-5 sm:py-5">
        <pre className="font-mono text-[12px] leading-[1.75] sm:text-[13px] sm:leading-[1.9]">
          {LINES.map((line, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: LINES は固定の定数配列で並べ替えも増減もない
            <Fragment key={index}>
              <div className="inou-term-line" style={{ animationDelay: `${index * STEP_MS}ms` }}>
                <LineBody line={line} />
              </div>
            </Fragment>
          ))}
          <div className="inou-term-line" style={{ animationDelay: `${LINES.length * STEP_MS}ms` }}>
            <span aria-hidden className="inou-term-caret text-brand-300">
              ▍
            </span>
          </div>
        </pre>
      </div>

      <p className="border-t border-line bg-surface-raised px-4 py-4 text-sm leading-relaxed text-ink-muted sm:px-5">
        {
          '作業カテゴリはAIが会話から判定し、資産化と請求の可否はプロジェクト設定に沿ってサーバーが決めます。エンジニアはどれも入力しません。記録した時点で、その月の予定と実績が返ります。'
        }
      </p>
    </div>
  );
}
